import { FunctionComponent, useCallback } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Container,
  FileButton,
  Grid,
  Group,
  Image,
  ScrollArea,
  Space,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  rem,
  useMantineTheme,
} from "@mantine/core";
import {
  DeleteOutline,
  Download,
  Google,
  MailRounded,
  PaletteOutlined,
  Search,
} from "@mui/icons-material";
import { GoogleIcon } from "src/components/common/Icons/GoogleIcon";
import { useDocuments } from "src/hooks/useDocuments";
import { useKnowledgebase } from "src/hooks/useKnowledgebase";
import { PlusIcon } from "@heroicons/react/24/solid";
import { useAppSelector } from "src/redux/redux-store/hooks";
import { StyledContainer } from "src/components/common/StyledComponents/Container";
import UploadDocs from "src/components/UploadDocs";
import { ErrorMessage } from "src/components/common/ErrorMessage/ErrorMessage";
import { StyledContainerInset } from "src/components/common/StyledComponents/BackgroundContainer";
import { COLORS, FONT_SIZES } from "src/utils/styles";
import { KnowledgebaseFilesDrawer } from "src/components/KnowledgebaseFilesDrawer";
import useResponsive from "src/hooks/useResponsive";
import {
  sendFailureNotification,
  sendSuccessNotification,
} from "src/utils/notifications";
import { NotificationTypes } from "src/enums/Notifications";
import { FileType, SubjectType } from "src/interfaces/KnowledgeBase";
import { useMultiFileUpload } from "src/hooks/useFileUpload";
import { Notifications } from "@mantine/notifications";
import ManageDocs from "src/components/ManageDocs";

const ManageKnowledgeBase: FunctionComponent = () => {
  const {
    files,
    loading,
    downloadFile,
    getDocs,
    error,
    dismissError,
    deleteSingleFile,
  } = useKnowledgebase();
  const {
    uploadKnowledgebaseFiles,
    uploadStatuses,
    resetUploadStatuses,
  } = useMultiFileUpload();

  const uploadDocs = async (
    file: File,
    level: string,
    type: FileType,
    subject: SubjectType
  ): Promise<boolean> => {
    try {
      // Use the uploadKnowledgebaseFiles function from the hook which includes validation
      const results = await uploadKnowledgebaseFiles(
        [file],
        level,
        type,
        subject
      );
      const success = results.every((result) => result);

      if (!success) {
        sendFailureNotification(
          NotificationTypes.fileUpload,
          "File upload failed. Please try again."
        );
      }
      return success;
    } catch (error) {
      console.error("Error during file upload:", error);
      sendFailureNotification(
        NotificationTypes.fileUpload,
        error instanceof Error
          ? error.message
          : "An error occurred during file upload. Please try again."
      );
      return false;
    } finally {
      getDocs();
    }
  };

  const { backgroundColor, backgroundColorExterior, isMobile } =
    useResponsive();

  return (
    <StyledContainer theme={{ backgroundColor: backgroundColorExterior }}>
      <StyledContainerInset theme={{ backgroundColor: backgroundColor }}>
        <Notifications
          position={isMobile ? "top-center" : "bottom-right"}
          limit={isMobile ? 1 : 5}
        />
        <div className="p-4 h-[100%] w-[100%] mobile:w-[100%] overflow-y-auto">
          <ErrorMessage error={error} handleClose={dismissError} />
          <KnowledgebaseFilesDrawer
            onPressClose={resetUploadStatuses}
            files={uploadStatuses}
          />
          <Text size={FONT_SIZES.h1} fw={600}>
            Manage Knowledge Base
          </Text>
          <Text c="dimmed" size={14}>
            Modify assistant’s core knowledge base.
          </Text>

          <ManageDocs
            files={files}
            onPressDownload={downloadFile}
            onRefresh={getDocs}
            deleteSingleFile={deleteSingleFile}
            loading={loading}
          />
        </div>
      </StyledContainerInset>
    </StyledContainer>
  );
};

export default ManageKnowledgeBase;
